import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Typography, Box, Avatar, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress,
} from '@mui/material';
import { Delete, Groups, WarningAmberRounded } from '@mui/icons-material';
import axios from 'axios';
import { BASE_URL, apiCall } from '../api/apiClient';

// ─── Shared Design Tokens ─────────────────────────────────────────────────────
const BG       = '#f4f7f5';
const SURFACE  = '#ffffff';
const BORDER   = '#d6e4de';
const ACCENT   = '#1e6b45';
const ACCENT_BG = '#e6f2ec';
const TEXT_PRI = '#0f1f14';
const TEXT_SEC = '#4b6358';

const TeamsTab = ({ event, onRefresh }) => {
  const teams = event?.entitiesParticipants || [];
  const games = event?.games || [];

  const [confirmDialog, setConfirmDialog] = useState({ open: false, team: null });
  const [deleting, setDeleting] = useState(false);

  // ── Access control ──────────────────────────────────────────────────────────
  const currentUserId = (() => {
    try {
      const user = localStorage.getItem('user');
      if (user) return JSON.parse(user)._id;
    } catch {}
    return null;
  })();

  const currentUserEntry = event.eventOwnersAndRoles?.find((entry) => {
    const entryUserId = typeof entry.userId === 'object' ? entry.userId._id : entry.userId;
    return entryUserId === currentUserId;
  });

  const isSuperAdmin = currentUserEntry?.roles?.includes('eventSuperAdmin');

  const getStats = (teamId) => {
    let wins = 0, losses = 0, draws = 0;
    games.forEach((g) => {
      const isE1 = g.entity1?._id === teamId;
      const isE2 = g.entity2?._id === teamId;
      if (!isE1 && !isE2) return;
      const s1 = g.score?.entity1 ?? null;
      const s2 = g.score?.entity2 ?? null;
      if (s1 === null || s2 === null) return;
      if (s1 === s2) draws++;
      else if (isE1) s1 > s2 ? wins++ : losses++;
      else s2 > s1 ? wins++ : losses++;
    });
    return { wins, losses, draws };
  };

  const getPlayers = (teamId) => {
    const players = new Set();
    games.forEach((g) => {
      [...(g.gameStartPlayerList || []), ...(g.players || [])].forEach((entry) => {
        if (entry.entityId?._id === teamId)
          (entry.userIds || []).forEach((u) => players.add(`${u.firstName} ${u.lastName}`));
      });
    });
    return [...players];
  };

  const openConfirm  = (team) => setConfirmDialog({ open: true, team });
  const closeConfirm = () => { if (!deleting) setConfirmDialog({ open: false, team: null }); };

  const handleDelete = async () => {
    const teamId = confirmDialog.team?._id;
    if (!teamId) return;
    setDeleting(true);
    try {
      const res = await apiCall((headers) =>
        axios.delete(`${BASE_URL}/events/${event._id}/remove-entity`, {
          headers,
          data: { entityId: teamId },
        })
      );
      const status = res?.status ?? res?.response?.status;
      if (status && status >= 400) throw new Error(res?.data?.message || 'Failed to remove team.');
      setConfirmDialog({ open: false, team: null });
      onRefresh();
    } catch (err) {
      const status = err?.response?.status;
      if (!status || status < 400) { setConfirmDialog({ open: false, team: null }); onRefresh(); return; }
      console.error('Failed to remove team:', err);
      alert(err?.response?.data?.message || 'Failed to remove team.');
    } finally {
      setDeleting(false);
    }
  };

  if (!teams.length) return (
    <Box sx={{ py: 6, textAlign: 'center', bgcolor: BG, borderRadius: 2 }}>
      <Groups sx={{ fontSize: 48, color: BORDER, mb: 1 }} />
      <Typography sx={{ color: TEXT_SEC }}>Aucune équipe enregistrée</Typography>
    </Box>
  );

  const thCell = (label, align = 'left') => (
    <TableCell align={align} sx={{
      color: TEXT_SEC, fontWeight: 700, fontSize: '0.68rem',
      textTransform: 'uppercase', letterSpacing: '0.08em',
      borderBottom: `1px solid ${BORDER}`, py: 1.5, bgcolor: BG,
    }}>
      {label}
    </TableCell>
  );

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ borderRadius: '12px', border: `1px solid ${BORDER}`, bgcolor: SURFACE, overflow: 'hidden' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {thCell('Équipe')}
              {thCell('Lieu')}
              {thCell('Joueurs')}
              {thCell('V', 'center')}
              {thCell('N', 'center')}
              {thCell('D', 'center')}
              {isSuperAdmin && thCell('Actions', 'center')}
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.map((team) => {
              const { wins, losses, draws } = getStats(team._id);
              const players = getPlayers(team._id);
              return (
                <TableRow
                  key={team._id}
                  hover
                  sx={{ '&:hover': { bgcolor: ACCENT_BG }, '& td': { borderColor: BORDER } }}
                >
                  {/* Équipe */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: ACCENT, color: '#fff', fontSize: 13, fontWeight: 700 }}>
                        {team.name?.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" fontWeight="600" sx={{ color: TEXT_PRI }}>
                        {team.name}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Lieu */}
                  <TableCell>
                    <Typography variant="body2" sx={{ color: TEXT_PRI }}>{team.city}</Typography>
                    <Typography variant="caption" sx={{ color: TEXT_SEC }}>{team.country}</Typography>
                  </TableCell>

                  {/* Joueurs */}
                  <TableCell>
                    {players.length > 0 ? (
                      <Tooltip title={players.join(', ')} arrow>
                        <Typography variant="caption" sx={{ cursor: 'pointer', color: ACCENT, textDecoration: 'underline' }}>
                          {players.length} joueur{players.length > 1 ? 's' : ''}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" sx={{ color: BORDER }}>—</Typography>
                    )}
                  </TableCell>

                  {/* V / N / D */}
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold" color="success.main">{wins}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold" sx={{ color: TEXT_SEC }}>{draws}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold" color="error.main">{losses}</Typography>
                  </TableCell>

                  {/* Actions */}
                  {isSuperAdmin && (
                    <TableCell align="center">
                      <Tooltip title="Retirer l'équipe de l'événement" arrow>
                        <IconButton
                          size="small"
                          onClick={() => openConfirm(team)}
                          sx={{
                            color: '#c62828', bgcolor: '#fdecea',
                            borderRadius: '8px',
                            '&:hover': { bgcolor: '#f9cbc8' },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: SURFACE, borderRadius: '16px',
            border: `1px solid ${BORDER}`,
            boxShadow: '0 8px 32px rgba(30,107,69,0.10)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: TEXT_PRI, fontWeight: 700 }}>
          <WarningAmberRounded color="error" />
          Retirer l'équipe
        </DialogTitle>
        <DialogContent sx={{ bgcolor: BG }}>
          <Typography variant="body2" sx={{ color: TEXT_SEC }}>
            Êtes-vous sûr de vouloir retirer{' '}
            <strong style={{ color: TEXT_PRI }}>{confirmDialog.team?.name}</strong>{' '}
            de cet événement ? Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, bgcolor: SURFACE }}>
          <Button
            onClick={closeConfirm}
            disabled={deleting}
            sx={{ color: TEXT_SEC, borderRadius: '10px', textTransform: 'none' }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
          >
            {deleting ? 'Suppression…' : 'Retirer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TeamsTab;